/**
 * Product settings → Card processing fees (TMMPDM-0089, tmm-api 0154 section G). Card processing is
 * a per-currency product setting, not a rate card term: one row per currency, each read with
 * `GET /v1/admin/billing/processing-fees/{currency}` and edited in place with a `PUT` that takes
 * effect now (a rate is never back-dated). A currency with no rate says so: no fee is posted without
 * one. The worksheet reads the rate server-side; the console computes no fee.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed } from '@/api/errors';
import type { ProcessingFeeRatesView } from '@/api/tmm/types';
import { ErrorState, SectionCard, SubmitButton, useNotify } from '@/components/page';
import { Amount } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { formatInstant, formatMoney, formatPercentBp, majorFromMinor } from '@/format';
import {
  CURRENCIES,
  invalidMoney,
  invalidPercent,
  parseMajor,
} from '@/routes/rateCards/rateCardInputs';

export function ProcessingFeesSection() {
  const { t } = useCopy();
  return (
    <SectionCard
      title={t('settings.processing.title', 'Card processing fees')}
      subtitle={t(
        'settings.processing.help',
        "What the card processor charges us on each collected charge, per currency. The reseller pays its margin's share. A change applies from now.",
      )}
      id="settings-processing-title"
    >
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('settings.processing.title', 'Card processing fees')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.processing.col.currency', 'Currency')}</TableCell>
              <TableCell align="right">{t('settings.processing.col.percent', 'Percent')}</TableCell>
              <TableCell align="right">
                {t('settings.processing.col.fixed', 'Fixed per charge')}
              </TableCell>
              <TableCell>{t('settings.processing.col.since', 'In force since')}</TableCell>
              <TableCell>{t('resellerCards.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {CURRENCIES.map((currency) => (
              <ProcessingFeeRow key={currency} currency={currency} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
}

function ProcessingFeeRow({ currency }: { currency: string }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const notify = useNotify();
  const [view, setView] = useState<ProcessingFeeRatesView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [token, setToken] = useState(0);
  const [editing, setEditing] = useState(false);
  const [percent, setPercent] = useState('');
  const [fixed, setFixed] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // After Save or Cancel, focus goes back to this row's Edit button (it remounts in the read row).
  const focusEdit = useRef(false);
  const leaveEdit = () => {
    focusEdit.current = true;
    setEditing(false);
  };

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getProcessingFees(currency)
      .then((next) => {
        if (cancelled) return;
        setView(next);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setView(null);
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [currency, token]);

  const startEdit = () => {
    const current = view?.current ?? null;
    setPercent(current ? String(current.percentBp / 100) : '');
    setFixed(current ? majorFromMinor(current.fixedMinor, currency) : '');
    setSaveError(null);
    setEditing(true);
  };

  const percentInvalid = invalidPercent(percent);
  const fixedInvalid = invalidMoney(fixed);

  const save = async () => {
    if (percentInvalid || fixedInvalid) return;
    setBusy(true);
    setSaveError(null);
    try {
      const next = await adminApi.setProcessingFee(currency, {
        percentBp: Math.round(Number(percent) * 100),
        fixedMinor: parseMajor(fixed, currency),
      });
      setView(next);
      leaveEdit();
      notify.success(
        t('settings.processing.saved', '{{currency}} card processing fee saved.', { currency }),
      );
    } catch (err) {
      setSaveError(
        isValidationFailed(err)
          ? t(
              'settings.processing.invalid',
              'Check the percent (0 to 100) and the fixed fee (0 or more).',
            )
          : errorCopy(
              err,
              t,
              t('settings.processing.error', "Couldn't save the card processing fee."),
            ),
      );
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <TableRow>
        <TableCell component="th" scope="row">
          {currency}
        </TableCell>
        <TableCell colSpan={4}>
          <ErrorState
            error={error}
            message={t('settings.processing.loadError', "Couldn't load the {{currency}} rate.", {
              currency,
            })}
            onRetry={() => setToken((n) => n + 1)}
          />
        </TableCell>
      </TableRow>
    );
  }

  const current = view?.current ?? null;
  const next = view?.scheduled[0];
  if (editing) {
    return (
      <TableRow>
        <TableCell component="th" scope="row">
          {currency}
        </TableCell>
        <TableCell align="right">
          <TextField
            size="small"
            label={t('settings.processing.percentLabel', '{{currency}} percent', { currency })}
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            error={percentInvalid}
            helperText={
              percentInvalid
                ? t('customerRateCards.form.between0And100', 'Must be between 0 and 100.')
                : undefined
            }
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
        </TableCell>
        <TableCell align="right">
          <TextField
            size="small"
            label={t('settings.processing.fixedLabel', '{{currency}} fixed per charge', {
              currency,
            })}
            value={fixed}
            onChange={(event) => setFixed(event.target.value)}
            error={fixedInvalid}
            helperText={
              fixedInvalid ? t('rateCards.form.nonNegative', 'Must be zero or greater.') : undefined
            }
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
        </TableCell>
        <TableCell colSpan={2}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <SubmitButton
                busy={busy}
                disabled={percentInvalid || fixedInvalid}
                onClick={() => void save()}
              >
                {t('settings.processing.save', 'Save')}
              </SubmitButton>
              <Button onClick={leaveEdit} disabled={busy}>
                {t('common.cancel', 'Cancel')}
              </Button>
            </Stack>
            {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          </Stack>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        {currency}
      </TableCell>
      {view === null ? (
        <TableCell colSpan={3}>{t('common.loading', 'Loading…')}</TableCell>
      ) : current === null ? (
        <TableCell colSpan={3}>
          {t('settings.processing.none', 'Not set: no processing fee is counted in {{currency}}.', {
            currency,
          })}
        </TableCell>
      ) : (
        <>
          <TableCell align="right">{formatPercentBp(current.percentBp, locale)}</TableCell>
          <TableCell align="right">
            <Amount minor={current.fixedMinor} currency={currency} />
          </TableCell>
          <TableCell>
            {formatInstant(current.effectiveFrom, locale)}
            {next ? (
              <Typography variant="caption" color="text.secondary" component="div">
                {t('settings.processing.next', 'Next: {{percent}} + {{fixed}} from {{when}}', {
                  percent: formatPercentBp(next.percentBp, locale),
                  fixed: formatMoney(next.fixedMinor, currency, locale),
                  when: formatInstant(next.effectiveFrom, locale),
                })}
              </Typography>
            ) : null}
          </TableCell>
        </>
      )}
      <TableCell>
        <Button
          size="small"
          onClick={startEdit}
          disabled={view === null}
          ref={(node: HTMLButtonElement | null) => {
            if (node && focusEdit.current) {
              focusEdit.current = false;
              node.focus();
            }
          }}
        >
          {t('settings.processing.edit', 'Edit {{currency}}…', { currency })}
        </Button>
      </TableCell>
    </TableRow>
  );
}
